/* eslint-disable sonarjs/cognitive-complexity */
import {
  BaSeInput,
  BaSeOptionPicker,
  BaSeSmall1,
  BaSeToggler,
  useDebounce,
} from '@base/react';
import * as Sebrae from '@sebrae/react-core';
import React from 'react';

import { useCityController } from '../../../../../../hooks/controllers/city-controller';
import { useUfController } from '../../../../../../hooks/controllers/uf-controller';
import { NewClassRoomContext } from '../../context/new-class-room-context';
import { AlignToggle, ContainerFilter, ContentToggle } from './filter-styled';
import UfFilterDisabled from './uf-filter-disabled';

const cache = Sebrae.StorageUtil.inTemporaryStore();
const cacheKeySebrae = '@empretec:sebrae';
const cacheKeyLastUf = '@empretec:lastUfSelected';
const cacheKeyLastCity = '@empretec:lastCitySelected';

function Filter() {
  const {
    filters,
    setFilters,
    interestedList,
    selectedInterestedList,
    setSelectedInterestedList,
  } = React.useContext(NewClassRoomContext);

  const [listUfs, setListUfs] = React.useState([]);

  const [selectedUF, setSelectedUF] = React.useState([]);
  const [selectedCITY, setSelectedCITY] = React.useState([]);
  const [ufGestorDefault, setUfGestorDefault] = React.useState([]);
  const [fieldOnResearch, setFieldOnResearch] = React.useState('');

  const { getUfs, returnSiglaById, returnUfBySigla } = useUfController();
  const { getCitiesByUf, rawCities } = useCityController();

  const fieldOfResearchDebounced = useDebounce(fieldOnResearch, 500);

  React.useEffect(() => {
    const load = async () => {
      try {
        const response = await getUfs(true);
        if (cache.getItem(cacheKeySebrae) !== 'NA') {
          const ufDef = await response.find(
            (res) => res.sigla === cache.getItem(cacheKeySebrae),
          );
          setUfGestorDefault([ufDef]);
          getCitiesByUf({ id: ufDef.id });
        }
        setListUfs(response);
      } catch (e) {
        setListUfs([]);
      }
    };
    load();
  }, [getUfs, getCitiesByUf]);

  const handleChange = React.useCallback(
    (name, value) => {
      setFilters((prev) => {
        return {
          ...prev,
          [name]: value,
        };
      });
    },
    [setFilters],
  );

  React.useEffect(() => {
    if (fieldOfResearchDebounced) {
      handleChange('fieldOfResearch', fieldOfResearchDebounced);
    }
  }, [fieldOfResearchDebounced, handleChange]);

  React.useEffect(() => {
    if (cache.hasItem(cacheKeyLastUf)) {
      setSelectedUF([cache.getItem(cacheKeyLastUf).id]);
      getCitiesByUf({ id: cache.getItem(cacheKeyLastUf).id });
    }
    if (cache.hasItem(cacheKeyLastCity)) {
      setSelectedCITY([cache.getItem(cacheKeyLastCity).id]);
    }
  }, [getCitiesByUf, returnUfBySigla]);

  const handleClearCity = React.useCallback(() => {
    setSelectedCITY([]);
    handleChange('city', '');
    cache.removeItem(cacheKeyLastCity);
  }, [handleChange]);

  const handleFilterInterested = React.useCallback(
    (id) => {
      setSelectedInterestedList([id]);
      setFilters((prev) => {
        return {
          ...prev,
          idClassRoom: id,
        };
      });
    },
    [setFilters, setSelectedInterestedList],
  );

  return (
    <ContainerFilter>
      <ContentToggle>
        <AlignToggle>
          <BaSeSmall1 isBold>Meus cadastros</BaSeSmall1>
          <BaSeToggler
            value={filters.myRecords}
            onChange={() => {
              setFilters((prev) => {
                return {
                  ...prev,
                  myRecords: !prev.myRecords,
                };
              });
            }}
          />
        </AlignToggle>

        <AlignToggle>
          <BaSeSmall1 isBold>Turmas desativadas</BaSeSmall1>
          <BaSeToggler
            value={filters.onlyDisableClassRoom}
            onChange={() => {
              setFilters((prev) => {
                return {
                  ...prev,
                  onlyDisableClassRoom: !prev.onlyDisableClassRoom,
                };
              });
            }}
          />
        </AlignToggle>
      </ContentToggle>

      <ContentToggle>
        <BaSeOptionPicker
          values={interestedList}
          label="Interessados na turma:"
          emptyValueLabel="Interessados na turma:"
          enableSelectAll={false}
          maxOptionsSelected={1}
          initialSelectedValuesId={selectedInterestedList}
          onChange={(values) => {
            if (values.length > 0) {
              handleFilterInterested(values[0].id);
            }
          }}
        />

        {cache.getItem(cacheKeySebrae) !== 'NA' && listUfs.length > 0 ? (
          <UfFilterDisabled listUfs={listUfs} defaultUf={ufGestorDefault} />
        ) : (
          <BaSeOptionPicker
            label="UF:"
            values={listUfs}
            emptyValueLabel="UF"
            maxOptionsSelected={1}
            width={100}
            initialSelectedValuesId={selectedUF}
            onChange={(values) => {
              if (values.length === 0) return;

              const siglaUf = returnSiglaById(
                Number(JSON.parse(values.map((val) => val.id))),
              );

              getCitiesByUf({ id: JSON.parse(values.map((val) => val.id)) });
              setSelectedUF(values.map((val) => val.id));
              handleChange('ufInitials', siglaUf);
              cache.setItem(cacheKeyLastUf, values[0]);
            }}
            extraActions={[
              {
                action: () => {
                  setSelectedUF([]);
                  handleChange('ufInitials', '');
                  cache.removeItem(cacheKeyLastUf);

                  setSelectedCITY([]);
                  handleChange('city', '');
                  cache.removeItem(cacheKeyLastCity);
                },
                label: 'Limpar',
              },
            ]}
          />
        )}

        <BaSeOptionPicker
          label="Cidade:"
          values={rawCities || []}
          emptyValueLabel="Cidade"
          maxOptionsSelected={1}
          initialSelectedValuesId={selectedCITY}
          onChange={(values) => {
            if (values.length === 0) return;
            const citySelected = values.find((val) => val);

            setSelectedCITY(values.map((val) => val.id));
            handleChange('city', citySelected.label);
            cache.setItem(cacheKeyLastCity, citySelected);
          }}
          extraActions={[
            {
              action: () => {
                handleClearCity();
              },
              label: 'Limpar',
            },
          ]}
        />

        {/* <BaSeInput
          label="‎ "
          width={400}
          placeholder="Pesquisar por nome, e-mail, CPF e telefone"
          value={fieldOnResearch}
          onChange={(e) => {
            setFieldOnResearch(e);
          }}
        /> */}

        {/* usar input com um botao de acao */}
        <BaSeInput
          align="start"
          color="default"
          placeholder="Pesquisar por nome, e-mail, CPF e telefone"
          width={320}
          iconButton={{
            action: () => {
              setFieldOnResearch('');
              handleChange('fieldOfResearch', '');
            },
            color: undefined,
            name: 'trash-alt-animated',
            type: 'primary',
            typeButton: 'base-icon',
            value: '',
          }}
          inputMode="text"
          label="‎ "
          size="medium"
          typeInput="text"
          value={fieldOnResearch}
          onChange={(e) => {
            setFieldOnResearch(e);
          }}
        />
      </ContentToggle>
    </ContainerFilter>
  );
}

export default Filter;
